<template>
  <div class="pa-4">
    <div class="d-flex align-center mb-4">
      <h2 class="text-h5 font-weight-bold">Cereri Concediu</h2>
      <v-spacer />
      <v-chip class="mr-3" color="green" variant="tonal">
        <v-icon start>mdi-umbrella-beach</v-icon>
        {{ zileCO }} zile CO disponibile ({{ anCurent }})
      </v-chip>
      <v-btn color="red-darken-1" prepend-icon="mdi-plus" @click="dialogCerere = true">
        Cerere nouă
      </v-btn>
    </div>

    <v-tabs v-model="tab" color="red-darken-1" class="mb-4">
      <v-tab value="cereri">Cereri mele</v-tab>
      <v-tab value="calendar">Calendar departament</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <!-- Grid cereri -->
      <v-window-item value="cereri">
        <DxDataGrid
          :data-source="dataSource"
          :show-borders="true"
          :row-alternation-enabled="true"
          :remote-operations="true"
        >
          <DxColumn data-field="DataInceput" caption="Data start" data-type="date" format="dd.MM.yyyy" :width="120" />
          <DxColumn data-field="DataSfarsit" caption="Data sfârșit" data-type="date" format="dd.MM.yyyy" :width="120" />
          <DxColumn data-field="NumarZile" caption="Zile" :width="70" />
          <DxColumn data-field="TipConcediu" caption="Tip" :width="100" />
          <DxColumn data-field="Descriere" caption="Descriere" />
          <DxColumn data-field="Stare" caption="Stare" :width="130" cell-template="stareCellTemplate" />
          <DxColumn caption="Acțiuni" :width="100" cell-template="actiuniCellTemplate" />

          <template #stareCellTemplate="{ data }">
            <v-chip :color="stareColor(data.data.Stare)" size="small" variant="tonal">
              {{ stareLabel(data.data.Stare) }}
            </v-chip>
          </template>

          <template #actiuniCellTemplate="{ data }">
            <v-btn
              v-if="data.data.Stare === 'in_asteptare'"
              icon="mdi-close"
              size="small"
              color="red"
              variant="text"
              @click="anuleaza(data.data.ID)"
              title="Anulează"
            />
          </template>

          <DxPaging :page-size="15" />
          <DxFilterRow :visible="true" />
        </DxDataGrid>
      </v-window-item>

      <!-- Calendar departament -->
      <v-window-item value="calendar">
        <div class="d-flex align-center mb-3 gap-2">
          <v-select v-model="calLuna" :items="luni" item-title="text" item-value="val" label="Luna" variant="outlined" density="compact" style="max-width:160px" />
          <v-select v-model="calAn" :items="ani" label="An" variant="outlined" density="compact" style="max-width:100px" @update:model-value="incarcaCalendar" />
          <v-btn icon="mdi-refresh" variant="text" @click="incarcaCalendar" />
        </div>
        <DxScheduler
          :data-source="calendarData"
          :current-date="calendarDate"
          :views="['month']"
          current-view="month"
          :height="500"
          :editing="false"
          start-date-expr="DataInceput"
          end-date-expr="DataSfarsit"
          text-expr="NumeAngajat"
        />
      </v-window-item>
    </v-window>

    <!-- Dialog cerere nouă -->
    <v-dialog v-model="dialogCerere" max-width="520" persistent>
      <v-card rounded="xl">
        <v-card-title class="pa-4">
          <v-icon class="mr-2" color="red-darken-1">mdi-umbrella-beach</v-icon>
          Cerere concediu nouă
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <v-form v-model="cerereValid" ref="cerereForm">
            <v-text-field v-model="cerereForm.TipConcediu" label="Tip concediu" variant="outlined" density="compact" class="mb-3" :rules="[required]" />
            <v-row>
              <v-col cols="6">
                <v-text-field v-model="cerereForm.DataInceput" label="Data start" type="date" variant="outlined" density="compact" :rules="[required]" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="cerereForm.DataSfarsit" label="Data sfârșit" type="date" variant="outlined" density="compact" :rules="[required]" />
              </v-col>
            </v-row>
            <v-textarea v-model="cerereForm.Descriere" label="Observații" variant="outlined" density="compact" rows="2" class="mt-2" />
          </v-form>
          <v-alert v-if="cerereError" type="error" variant="tonal" class="mt-2">{{ cerereError }}</v-alert>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn variant="text" @click="dialogCerere = false">Anulează</v-btn>
          <v-spacer />
          <v-btn color="red-darken-1" :loading="cerereLoading" :disabled="!cerereValid" @click="submitCerere">
            Trimite cerere
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { DxDataGrid, DxColumn, DxPaging, DxFilterRow } from 'devextreme-vue/data-grid'
import { DxScheduler } from 'devextreme-vue/scheduler'
import DataSource from 'devextreme/data/data_source'
import PORTAL_CereriConcedii from '@/services/Portal/PORTAL_CereriConcedii'

export default defineComponent({
  name: 'CereriConcediu',
  components: { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxScheduler },
  data() {
    const now = new Date();
    return {
      tab: 'cereri',
      anCurent: now.getFullYear(),
      zileCO: 0,
      dataSource: new DataSource({ store: PORTAL_CereriConcedii.getODataStore() }),
      calendarData: [] as any[],
      calLuna: now.getMonth() + 1,
      calAn: now.getFullYear(),
      calendarDate: now,
      dialogCerere: false,
      cerereValid: false,
      cerereLoading: false,
      cerereError: '',
      cerereForm: { TipConcediu: '', DataInceput: '', DataSfarsit: '', Descriere: '' },
      luni: [
        { val: 1, text: 'Ianuarie' }, { val: 2, text: 'Februarie' }, { val: 3, text: 'Martie' },
        { val: 4, text: 'Aprilie' }, { val: 5, text: 'Mai' }, { val: 6, text: 'Iunie' },
        { val: 7, text: 'Iulie' }, { val: 8, text: 'August' }, { val: 9, text: 'Septembrie' },
        { val: 10, text: 'Octombrie' }, { val: 11, text: 'Noiembrie' }, { val: 12, text: 'Decembrie' }
      ],
      ani: [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1]
    }
  },
  async mounted() {
    const resp = await PORTAL_CereriConcedii.getZileCORamase(this.anCurent);
    this.zileCO = resp.data ?? 0;
    await this.incarcaCalendar();
  },
  methods: {
    required(v: any) { return !!v || 'Câmp obligatoriu' },
    stareColor(stare: string) {
      const map: Record<string, string> = { in_asteptare: 'amber', aprobata: 'green', respinsa: 'red', '-2': 'grey' };
      return map[stare] ?? 'grey';
    },
    stareLabel(stare: string) {
      const map: Record<string, string> = { in_asteptare: 'În așteptare', aprobata: 'Aprobată', respinsa: 'Respinsă', '-2': 'Anulată' };
      return map[stare] ?? stare;
    },
    async anuleaza(id: string) {
      await PORTAL_CereriConcedii.anuleaza(id);
      this.dataSource.reload();
    },
    async incarcaCalendar() {
      this.calendarDate = new Date(this.calAn, this.calLuna - 1, 1);
      const resp = await PORTAL_CereriConcedii.getCalendarDepartament('', this.calAn, this.calLuna);
      this.calendarData = resp.data ?? [];
    },
    async submitCerere() {
      this.cerereLoading = true;
      this.cerereError = '';
      try {
        await PORTAL_CereriConcedii.add(this.cerereForm);
        this.dialogCerere = false;
        this.dataSource.reload();
      } catch (e: any) {
        this.cerereError = e?.response?.data?.message ?? 'Eroare la trimiterea cererii.';
      } finally {
        this.cerereLoading = false;
      }
    }
  }
})
</script>
